import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  isAdminAgentEnabled,
  isChangeAgentEnabled,
} from '../../utils/flagApplicationFeatures';

interface AppFeatureState {
  adminAgentEnabled: boolean;
  changeAgentEnabled: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: AppFeatureState = {
  adminAgentEnabled: false,
  changeAgentEnabled: false,
  loading: false,
  error: null,
};

export const fetchAppFeatures = createAsyncThunk<
  { adminAgentEnabled: boolean; changeAgentEnabled: boolean },
  void,
  { rejectValue: string }
>('appFeature/fetchAppFeatures', async (_, { rejectWithValue }) => {
  try {
    const [adminAgentEnabled, changeAgentEnabled] = await Promise.all([
      isAdminAgentEnabled(),
      isChangeAgentEnabled(),
    ]);
    return { adminAgentEnabled, changeAgentEnabled };
  } catch (err) {
    return rejectWithValue(
      (err as Error).message || 'Failed to fetch app features'
    );
  }
});

const appFeatureSlice = createSlice({
  name: 'appFeature',
  initialState,
  reducers: {
    setAdminAgentEnabled: (state, action: PayloadAction<boolean>) => {
      state.adminAgentEnabled = action.payload;
    },
    setChangeAgentEnabled: (state, action: PayloadAction<boolean>) => {
      state.changeAgentEnabled = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAppFeatures.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAppFeatures.fulfilled, (state, action) => {
        state.loading = false;
        state.adminAgentEnabled = action.payload.adminAgentEnabled;
        state.changeAgentEnabled = action.payload.changeAgentEnabled;
      })
      .addCase(fetchAppFeatures.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch app features';
      });
  },
});

export const {
  setAdminAgentEnabled,
  setChangeAgentEnabled,
  setLoading,
  setError,
} = appFeatureSlice.actions;
export default appFeatureSlice.reducer;
